import { skipToken } from "@tanstack/react-query";

import { ClientConfig } from "../../../../commons/libs/externals/veloconnect-proxy";
import {
  getVeloconnectEndpointByBrandKey,
  getVeloconnectErrorType,
  isVeloconnectImportReady
} from "../../../../commons/libs/veloconnect-service";
import useQuery from "../hooks/use-query";

import { useSettings } from "./use-settings";

/** A hook that fetches a Veloconnect Endpoint for a given brandKey and provides utility functions to interact with it. */
const useVeloconnectEndpoint = (
  // When used from `ProductDetailsPartial` the `brandKey` is `undefined`, until the `product` is loaded.
  brandKey: string | undefined
) => {
  const settings = useSettings();

  const query = useQuery<ClientConfig | null>({
    queryKey: ["veloconnect-endpoint", brandKey] as const,
    queryFn: brandKey
      ? async () => {
          const endpoint = await getVeloconnectEndpointByBrandKey(brandKey);
          // If no endpoint is found for the brand, the server will return an empty object.
          // If this is the case, explicitly return `null`,
          // to indicate that there is no Veloconnect available for this brand.
          const doesEndpointExistForBrand = endpoint && Object.keys(endpoint).length > 0;
          return doesEndpointExistForBrand ? endpoint : null;
        }
      : skipToken,
    onError: error => console.error("Error fetching Veloconnect endpoint", error)
  });

  const endpoint = query.data;

  // Show the notification if the Veloconnect is not configured but available for configuration, or if it is restricted.
  const shouldShowNotification = Boolean(
    !!endpoint && !endpoint.isConfigured && !settings.initialized.veloconnect[endpoint?.endpointKey]?.modalDismissed
  );

  const isImportReady = isVeloconnectImportReady(endpoint ?? undefined);

  const isImportOutdated = endpoint?.adapters?.some(adapter => adapter.isImportOutdated) || false;

  return {
    query,
    error: getVeloconnectErrorType(query.error),
    isImportReady,
    isImportOutdated,
    shouldShowNotification
  };
};

export type UseVeloconnectEndpointReturn = ReturnType<typeof useVeloconnectEndpoint>;

export default useVeloconnectEndpoint;
